import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Play, RefreshCw, Loader2, CheckCircle2, XCircle, AlertCircle, Plus, Trash2,
  Wand2, Code, Film, Eye, Camera, Clock,
} from "lucide-react";

const STATUS_CLR = {
  pass: "text-emerald-700 border-emerald-300 bg-emerald-50",
  fail: "text-rose-700 border-rose-300 bg-rose-50",
  error: "text-orange-700 border-orange-300 bg-orange-50",
  running: "text-amber-700 border-amber-300 bg-amber-50",
};
const fmtIST = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false }) : "—";

export default function AdminQaCenter() {
  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <span className="overline">Quality Center</span>
          <h1 className="font-display text-2xl sm:text-3xl font-bold mt-2" data-testid="qa-center-title">QA Test Center</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Auto-discover every API in the platform, author test cases, run end-to-end browser flows with Playwright (screenshots + video), and surface regressions on every deploy.
          </p>
        </div>

        <Tabs defaultValue="api">
          <TabsList>
            <TabsTrigger value="api" data-testid="qa-tab-api">API Tests</TabsTrigger>
            <TabsTrigger value="browser" data-testid="qa-tab-browser">Browser Flows</TabsTrigger>
          </TabsList>
          <TabsContent value="api" className="mt-4"><ApiTestsTab /></TabsContent>
          <TabsContent value="browser" className="mt-4"><BrowserFlowsTab /></TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// API TESTS TAB
// ═══════════════════════════════════════════════════════════════════════════
function ApiTestsTab() {
  const [cases, setCases] = useState([]);
  const [runs, setRuns] = useState([]);
  const [discover, setDiscover] = useState({ routes: [], count: 0 });
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pathPrefix, setPathPrefix] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [c, r, d] = await Promise.all([
        api.get("/qa/api/cases", { params: { limit: 1000 } }),
        api.get("/qa/api/runs", { params: { limit: 100 } }),
        api.get("/qa/api/discover"),
      ]);
      setCases(c.data.items || []);
      setRuns(r.data.items || []);
      setDiscover({ routes: d.data.routes || [], count: d.data.count });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return pathPrefix ? cases.filter((c) => c.path.startsWith(pathPrefix)) : cases;
  }, [cases, pathPrefix]);

  const stats = useMemo(() => {
    const last24h = runs.filter((r) => Date.now() - new Date(r.created_at).getTime() < 24 * 3600 * 1000);
    return {
      total_cases: cases.length,
      enabled: cases.filter((c) => c.enabled).length,
      pass_24h: last24h.filter((r) => r.status === "pass").length,
      fail_24h: last24h.filter((r) => r.status !== "pass").length,
    };
  }, [cases, runs]);

  const runOne = async (id) => {
    setRunning(true);
    try {
      const { data } = await api.post(`/qa/api/run/${id}`);
      toast.success(`${data.status} · HTTP ${data.http_status} · ${data.elapsed_ms}ms`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const runAll = async () => {
    if (!window.confirm(`Run all ${stats.enabled} enabled cases? This may take a minute.`)) return;
    setRunning(true);
    try {
      const { data } = await api.post("/qa/api/run-all", null, { params: { concurrency: 5 } });
      const s = data.summary || {};
      toast.success(`Batch done: ${s.pass || 0} pass · ${s.fail || 0} fail · ${s.error || 0} error`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const autogen = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/qa/api/cases/autogen", null, { params: { replace: true } });
      toast.success(`Auto-generated ${data.created} smoke tests (${data.skipped} skipped — already covered)`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this test case?")) return;
    try { await api.delete(`/qa/api/cases/${id}`); load(); toast.success("Deleted"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Routes discovered" value={discover.count} icon={Wand2} />
        <Stat label="Test cases" value={stats.total_cases} icon={Code} accent="text-blue-700" />
        <Stat label="Passing (24h)" value={stats.pass_24h} icon={CheckCircle2} accent="text-emerald-700" />
        <Stat label="Failing (24h)" value={stats.fail_24h} icon={XCircle} accent="text-rose-700" />
      </div>

      <div className="surface-card p-4 flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-muted-foreground">Filter by path prefix</label>
          <Input value={pathPrefix} onChange={(e)=>setPathPrefix(e.target.value)} placeholder="/api/admin/..." className="mt-1" data-testid="qa-path-filter" />
        </div>
        <Button variant="outline" onClick={autogen} disabled={running} data-testid="qa-autogen-btn">
          <Wand2 className="h-4 w-4 mr-1.5" /> Auto-generate smoke tests
        </Button>
        <Button variant="outline" onClick={() => { setEditing({ name: "", method: "GET", path: "", role: "admin", expected_status: 200, enabled: true }); setEditorOpen(true); }} data-testid="qa-add-case-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New test case
        </Button>
        <Button onClick={runAll} disabled={running || stats.enabled === 0} data-testid="qa-run-all-btn">
          {running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />}
          Run all ({stats.enabled})
        </Button>
      </div>

      <div className="surface-card overflow-x-auto">
        {loading ? <div className="p-8 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div> :
          filtered.length === 0 ? <div className="p-8 text-center text-muted-foreground">No test cases yet. Click "Auto-generate smoke tests" to discover them from OpenAPI.</div> :
          <table className="w-full text-sm">
            <thead className="bg-accent/50 text-[11px] uppercase tracking-widest text-muted-foreground">
              <tr><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2">Method</th><th className="px-3 py-2 text-left">Path</th><th className="px-3 py-2">Role</th><th className="px-3 py-2">Expect</th><th className="px-3 py-2">Last run</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((c) => {
                const lastRun = runs.find((r) => r.case_id === c.id);
                return (
                  <tr key={c.id} className="hover:bg-accent/30" data-testid={`qa-case-${c.id}`}>
                    <td className="px-3 py-2">{c.name} {c.is_auto_generated && <Badge variant="outline" className="text-[9px] ml-1">auto</Badge>}</td>
                    <td className="px-3 py-2 text-center"><Badge variant="outline" className="text-[10px]">{c.method}</Badge></td>
                    <td className="px-3 py-2 font-mono text-xs">{c.path}</td>
                    <td className="px-3 py-2 text-center text-xs">{c.role || "—"}</td>
                    <td className="px-3 py-2 text-center text-xs">{c.expected_status}</td>
                    <td className="px-3 py-2 text-center">
                      {lastRun ? <Badge variant="outline" className={`text-[10px] ${STATUS_CLR[lastRun.status]}`}>{lastRun.status} · {lastRun.http_status}</Badge> : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                    <td className="px-3 py-2 flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => runOne(c.id)} disabled={running} data-testid={`qa-run-${c.id}`}><Play className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => { setEditing(c); setEditorOpen(true); }}><Code className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => del(c.id)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>}
      </div>

      {/* Recent runs */}
      <div>
        <div className="font-semibold text-sm mb-2">Recent runs</div>
        <div className="surface-card overflow-x-auto max-h-96">
          <table className="w-full text-xs">
            <thead className="bg-accent/50 text-[10px] uppercase tracking-widest text-muted-foreground">
              <tr><th className="px-2 py-1 text-left">When</th><th className="px-2 py-1 text-left">Case</th><th className="px-2 py-1">Status</th><th className="px-2 py-1">HTTP</th><th className="px-2 py-1">Time</th><th className="px-2 py-1 text-left">Error/Response</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {runs.slice(0, 50).map((r) => (
                <tr key={r.id}>
                  <td className="px-2 py-1 text-muted-foreground whitespace-nowrap">{fmtIST(r.created_at)}</td>
                  <td className="px-2 py-1">{r.case_name}</td>
                  <td className="px-2 py-1 text-center"><Badge variant="outline" className={`text-[9px] ${STATUS_CLR[r.status]}`}>{r.status}</Badge></td>
                  <td className="px-2 py-1 text-center font-mono">{r.http_status}</td>
                  <td className="px-2 py-1 text-center font-mono">{r.elapsed_ms}ms</td>
                  <td className="px-2 py-1 font-mono text-[10px] truncate max-w-[400px]">{r.error || r.response_snippet?.slice(0, 100)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CaseEditor open={editorOpen} setOpen={setEditorOpen} initial={editing} onSaved={() => { setEditorOpen(false); load(); }} discover={discover.routes} />
    </div>
  );
}

function CaseEditor({ open, setOpen, initial, onSaved, discover }) {
  const [form, setForm] = useState(initial || {});
  useEffect(() => { setForm(initial || {}); }, [initial]);
  if (!open) return null;
  const isEdit = !!form.id;
  const save = async () => {
    try {
      const body = { ...form, method: (form.method || "GET").toUpperCase() };
      if (isEdit) await api.put(`/qa/api/cases/${form.id}`, body);
      else await api.post("/qa/api/cases", body);
      toast.success(isEdit ? "Updated" : "Created");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit test case" : "New API test case"}</DialogTitle>
          <DialogDescription>Pick a method+path, set expected status, and optionally a JSON payload.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs">Name</label><Input value={form.name || ""} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="qa-case-name" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><label className="text-xs">Method</label>
              <Select value={form.method || "GET"} onValueChange={(v)=>setForm({...form, method: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["GET","POST","PUT","PATCH","DELETE"].map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="col-span-2"><label className="text-xs">Path</label>
              <Input value={form.path || ""} onChange={(e)=>setForm({...form, path: e.target.value})} list="qa-path-suggestions" placeholder="/api/admin/customers" />
              <datalist id="qa-path-suggestions">{(discover || []).map((r,i)=><option key={i} value={r.path}>{r.method} · {r.tags?.join(",")}</option>)}</datalist>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs">Role (auth as)</label>
              <Select value={form.role || "admin"} onValueChange={(v)=>setForm({...form, role: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["admin","partner","l1","l2","l3"].map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><label className="text-xs">Expected HTTP status</label><Input type="number" value={form.expected_status || 200} onChange={(e)=>setForm({...form, expected_status: parseInt(e.target.value)})} /></div>
          </div>
          <div><label className="text-xs">Body must contain (optional)</label><Input value={form.expected_body_contains || ""} onChange={(e)=>setForm({...form, expected_body_contains: e.target.value})} placeholder='e.g. "items":' /></div>
          <div><label className="text-xs">JSON Payload (for POST/PUT/PATCH)</label><Textarea value={form.payload ? JSON.stringify(form.payload, null, 2) : ""} onChange={(e)=>{ try { setForm({...form, payload: e.target.value ? JSON.parse(e.target.value) : null}); } catch {} }} rows={5} className="font-mono text-xs" /></div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="qa-case-save">{isEdit ? "Update" : "Create"}</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// BROWSER FLOWS TAB
// ═══════════════════════════════════════════════════════════════════════════
function BrowserFlowsTab() {
  const [flows, setFlows] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewRun, setViewRun] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [f, r] = await Promise.all([api.get("/qa/browser/flows"), api.get("/qa/browser/runs", { params: { limit: 50 } })]);
      setFlows(f.data.items || []);
      setRuns(r.data.items || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  const seedPrebuilt = async () => {
    try { const { data } = await api.post("/qa/browser/flows/seed-prebuilt"); toast.success(`Seeded ${data.created} new, ${data.updated} updated`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const runFlow = async (id) => { setRunning(true); try { await api.post(`/qa/browser/run/${id}`); toast.success("Run queued — Playwright is launching"); setTimeout(load, 3000); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setRunning(false); } };
  const runAll = async () => { if (!window.confirm("Run all enabled flows in parallel?")) return; setRunning(true); try { const { data } = await api.post("/qa/browser/run-all"); toast.success(`Queued ${data.queued} flows`); setTimeout(load, 3000); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setRunning(false); } };
  const del = async (id) => { if (!window.confirm("Delete this flow?")) return; try { await api.delete(`/qa/browser/flows/${id}`); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Flows" value={flows.length} icon={Film} accent="text-blue-700" />
        <Stat label="Pass (24h)" value={runs.filter((r) => r.status === "pass" && Date.now() - new Date(r.created_at).getTime() < 24*3600*1000).length} icon={CheckCircle2} accent="text-emerald-700" />
        <Stat label="Fail (24h)" value={runs.filter((r) => r.status !== "pass" && r.status !== "running" && Date.now() - new Date(r.created_at).getTime() < 24*3600*1000).length} icon={XCircle} accent="text-rose-700" />
        <Stat label="Running now" value={runs.filter((r) => r.status === "running").length} icon={Loader2} accent="text-amber-700" />
      </div>

      <div className="surface-card p-4 flex flex-wrap gap-2">
        <Button variant="outline" onClick={seedPrebuilt} data-testid="qa-seed-prebuilt-btn">Seed prebuilt flows</Button>
        <Button variant="outline" onClick={() => { setEditing({ name: "", role: "admin", steps: [{ action: "goto", url: "/login" }], enabled: true, record_video: true }); setEditorOpen(true); }} data-testid="qa-add-flow-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New flow
        </Button>
        <Button onClick={runAll} disabled={running || flows.length === 0} data-testid="qa-run-all-flows-btn">
          {running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />}Run all
        </Button>
      </div>

      <div className="surface-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-accent/50 text-[11px] uppercase tracking-widest text-muted-foreground">
            <tr><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2">Role</th><th className="px-3 py-2">Steps</th><th className="px-3 py-2">Video</th><th className="px-3 py-2">Last run</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {flows.map((f) => {
              const last = runs.find((r) => r.flow_id === f.id);
              return (
                <tr key={f.id} data-testid={`qa-flow-${f.id}`}>
                  <td className="px-3 py-2">{f.name}{f.is_prebuilt && <Badge variant="outline" className="text-[9px] ml-1.5">prebuilt</Badge>}<div className="text-xs text-muted-foreground">{f.description}</div></td>
                  <td className="px-3 py-2 text-center text-xs">{f.role}</td>
                  <td className="px-3 py-2 text-center text-xs">{(f.steps || []).length}</td>
                  <td className="px-3 py-2 text-center">{f.record_video ? <Film className="h-3.5 w-3.5 mx-auto" /> : <Camera className="h-3.5 w-3.5 mx-auto text-muted-foreground" />}</td>
                  <td className="px-3 py-2 text-center">
                    {last ? <Badge variant="outline" className={`text-[10px] ${STATUS_CLR[last.status]}`}>{last.status === "running" ? <><Loader2 className="h-3 w-3 mr-1 animate-spin inline" />running</> : last.status}{last.elapsed_ms ? ` · ${(last.elapsed_ms/1000).toFixed(1)}s` : ""}</Badge> : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2 flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => runFlow(f.id)} disabled={running} data-testid={`qa-flow-run-${f.id}`}><Play className="h-3.5 w-3.5" /></Button>
                    {last && <Button size="sm" variant="ghost" onClick={() => setViewRun(last.id)} data-testid={`qa-flow-view-${f.id}`}><Eye className="h-3.5 w-3.5" /></Button>}
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(f); setEditorOpen(true); }}><Code className="h-3.5 w-3.5" /></Button>
                    {!f.is_prebuilt && <Button size="sm" variant="ghost" onClick={() => del(f.id)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Recent runs */}
      <div>
        <div className="font-semibold text-sm mb-2 flex items-center justify-between">
          <span>Recent browser runs</span>
          <Button size="sm" variant="ghost" onClick={load}><RefreshCw className="h-3.5 w-3.5" /></Button>
        </div>
        <div className="surface-card overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-accent/50 text-[10px] uppercase tracking-widest text-muted-foreground">
              <tr><th className="px-2 py-1 text-left">When</th><th className="px-2 py-1 text-left">Flow</th><th className="px-2 py-1">Status</th><th className="px-2 py-1">Duration</th><th className="px-2 py-1">Failed step</th><th className="px-2 py-1"></th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {runs.slice(0, 30).map((r) => (
                <tr key={r.id}>
                  <td className="px-2 py-1 text-muted-foreground">{fmtIST(r.created_at)}</td>
                  <td className="px-2 py-1">{r.flow_name}</td>
                  <td className="px-2 py-1 text-center"><Badge variant="outline" className={`text-[9px] ${STATUS_CLR[r.status]}`}>{r.status}</Badge></td>
                  <td className="px-2 py-1 text-center font-mono">{r.elapsed_ms ? `${(r.elapsed_ms/1000).toFixed(1)}s` : "—"}</td>
                  <td className="px-2 py-1 text-center font-mono">{r.failed_at_step ?? "—"}</td>
                  <td className="px-2 py-1 text-right"><Button size="sm" variant="ghost" onClick={() => setViewRun(r.id)}><Eye className="h-3.5 w-3.5" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editorOpen && <FlowEditor open={editorOpen} setOpen={setEditorOpen} initial={editing} onSaved={() => { setEditorOpen(false); load(); }} />}
      {viewRun && <RunViewer runId={viewRun} onClose={() => setViewRun(null)} token={localStorage.getItem("payflex_token")} />}
    </div>
  );
}

function FlowEditor({ open, setOpen, initial, onSaved }) {
  const [form, setForm] = useState(initial || { steps: [] });
  const [stepsJson, setStepsJson] = useState(JSON.stringify(initial?.steps || [], null, 2));
  const save = async () => {
    let steps;
    try { steps = JSON.parse(stepsJson); }
    catch { toast.error("Steps JSON is invalid"); return; }
    try {
      const body = { ...form, steps };
      if (form.id) await api.put(`/qa/browser/flows/${form.id}`, body);
      else await api.post("/qa/browser/flows", body);
      toast.success("Saved"); onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{form.id ? "Edit flow" : "New browser flow"}</DialogTitle>
          <DialogDescription>Configure flow metadata and author DSL steps.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs">Name</label><Input value={form.name || ""} onChange={(e)=>setForm({...form, name: e.target.value})} /></div>
          <div><label className="text-xs">Description</label><Input value={form.description || ""} onChange={(e)=>setForm({...form, description: e.target.value})} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs">Role</label>
              <Select value={form.role || "admin"} onValueChange={(v)=>setForm({...form, role: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["admin","partner","l1","l2","l3"].map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="flex items-center pt-5 gap-3"><label className="text-xs flex items-center gap-1"><input type="checkbox" checked={form.record_video !== false} onChange={(e)=>setForm({...form, record_video: e.target.checked})} /> Record video</label><label className="text-xs flex items-center gap-1"><input type="checkbox" checked={form.enabled !== false} onChange={(e)=>setForm({...form, enabled: e.target.checked})} /> Enabled</label></div>
          </div>
          <div>
            <label className="text-xs">Steps (JSON array)</label>
            <Textarea value={stepsJson} onChange={(e)=>setStepsJson(e.target.value)} rows={14} className="font-mono text-[11px]" />
            <div className="text-[10px] text-muted-foreground mt-1">Actions: <span className="font-mono">goto</span>, <span className="font-mono">click</span>, <span className="font-mono">fill</span>, <span className="font-mono">wait</span>, <span className="font-mono">wait_for</span>, <span className="font-mono">expect_visible</span>, <span className="font-mono">expect_text</span>, <span className="font-mono">expect_url_contains</span>, <span className="font-mono">screenshot</span>. Placeholders: <span className="font-mono">{`{{email}}`}</span>, <span className="font-mono">{`{{password}}`}</span>.</div>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button onClick={save}>Save</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RunViewer({ runId, onClose, token }) {
  const [run, setRun] = useState(null);
  useEffect(() => {
    const fetchRun = async () => { try { const { data } = await api.get(`/qa/browser/runs/${runId}`); setRun(data); } catch {} };
    fetchRun();
    const t = setInterval(() => { if (run?.status === "running") fetchRun(); }, 3000);
    return () => clearInterval(t);
  }, [runId]); // eslint-disable-line
  if (!run) return null;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const artifactUrl = (filename) => `${base}/api/qa/browser/runs/${runId}/artifacts/${filename}?token=${encodeURIComponent(token || "")}`;
  const videoFilename = run.video_path ? run.video_path.split("/").pop() : null;
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{run.flow_name}</DialogTitle>
          <DialogDescription>Run details, video and step-by-step screenshots.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="flex flex-wrap gap-3 items-center">
            <Badge variant="outline" className={STATUS_CLR[run.status]}>{run.status}</Badge>
            <span className="text-xs text-muted-foreground"><Clock className="h-3 w-3 inline mr-1" />{run.elapsed_ms ? `${(run.elapsed_ms/1000).toFixed(2)}s` : "—"}</span>
            <span className="text-xs">Steps: <b>{(run.step_log || []).length}</b></span>
            {run.failed_at_step !== null && <span className="text-xs text-rose-700">Failed at step #{run.failed_at_step}</span>}
          </div>
          {run.error && <div className="surface-card p-3 bg-rose-50 border-rose-200 text-rose-800 text-xs font-mono whitespace-pre-wrap">{run.error}</div>}
          {videoFilename && (
            <div>
              <div className="text-xs font-medium mb-1 flex items-center gap-2"><Film className="h-3.5 w-3.5" />Video</div>
              <video controls className="w-full rounded-md border max-h-96" src={artifactUrl(videoFilename)} />
            </div>
          )}
          <div>
            <div className="text-xs font-medium mb-1">Step timeline</div>
            <div className="space-y-2">
              {(run.step_log || []).map((s, i) => (
                <div key={i} className={`surface-card p-3 ${s.ok ? "" : "border-rose-300 bg-rose-50"}`}>
                  <div className="flex items-center gap-2 text-xs">
                    {s.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <XCircle className="h-3.5 w-3.5 text-rose-600" />}
                    <span className="font-mono">#{s.i} {s.action}</span>
                    <span className="text-muted-foreground font-mono">{s.ms}ms</span>
                  </div>
                  {s.error && <pre className="text-[10px] mt-1 font-mono whitespace-pre-wrap text-rose-700">{s.error}</pre>}
                  {s.screenshot && <img src={artifactUrl(s.screenshot)} alt="step" className="mt-2 rounded border max-h-48" />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, icon: Icon, accent = "text-muted-foreground" }) {
  return (
    <div className="surface-card p-4 flex items-center justify-between">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className={`text-2xl font-display font-bold mt-1 ${accent}`}>{value}</div>
      </div>
      <Icon className={`h-6 w-6 ${accent} opacity-60`} />
    </div>
  );
}
